import { useState } from "react";
import { aboutData } from "@/mocks/about";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function AboutPage() {
  const [formState, setFormState] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormState("success");
      setFormMessage("感谢您的留言，我们会尽快回复！");
      return;
    }

    formData.delete("phone_alt");

    setFormState("loading");
    setFormMessage("");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9phk8li2vhsdh2d34kg", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormState("success");
        setFormMessage("感谢您的留言，我们会尽快回复！");
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormState("error");
        setFormMessage(serverMsg.includes("spam") ? "提交失败，请稍后重试" : (serverMsg || "提交失败，请稍后重试"));
      }
    } catch {
      setFormState("error");
      setFormMessage("网络错误，请检查网络后重试");
    }
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于17.c 起草在线观看免费版",
    description: "了解17.c 起草在线观看免费版的品牌故事、用户口碑和联系方式。专业的影视在线观看平台。",
    url: "https://17c-drafting.example.com/about",
    isPartOf: {
      "@type": "WebSite",
      name: "17.c 起草在线观看免费版",
      url: "https://17c-drafting.example.com",
    },
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "17.c 起草在线观看",
    url: "https://17c-drafting.example.com",
    email: aboutData.contact.email,
    telephone: aboutData.contact.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: "建国路88号SOHO现代城A座",
      addressLocality: "北京",
      addressRegion: "朝阳区",
      addressCountry: "CN",
    },
    openingHours: "Mo-Su 09:00-21:00",
  };

  return (
    <main className="pt-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />

      {/* Brand Intro */}
      <section className="py-10 md:py-16 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14">
            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
                {aboutData.brandIntro.title}
              </h1>
              <p className="text-sm text-primary-500 font-medium mb-4">{aboutData.brandIntro.subtitle}</p>
              <p className="text-foreground-600 leading-relaxed mb-6">
                {aboutData.brandIntro.description}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {aboutData.brandIntro.features.map((feat) => (
                  <div key={feat.title} className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className={`${feat.icon} text-primary-600 text-base`}></i>
                    </div>
                    <div>
                      <p className="font-medium text-foreground-900 text-sm mb-1">{feat.title}</p>
                      <p className="text-xs text-foreground-500 leading-relaxed">{feat.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-1 w-full">
              <div className="rounded-2xl overflow-hidden w-full h-[320px] md:h-[400px]">
                <img
                  src={aboutData.brandIntro.image}
                  alt="17.c 起草在线观看团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-10 md:py-12 bg-background-100/70">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {aboutData.reputation.stats.map((stat) => (
              <div key={stat.label} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                <p className="font-heading text-2xl md:text-3xl font-bold text-accent-500 mb-1">{stat.value}</p>
                <p className="text-xs text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Reviews */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {aboutData.reputation.title}
            </h2>
            <p className="text-foreground-500 text-sm">{aboutData.reputation.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {aboutData.reputation.reviews.map((review, index) => (
              <div key={index} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-medium">
                      {review.name.charAt(0)}
                    </div>
                    <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`text-xs ${i < review.rating ? "ri-star-fill text-accent-400" : "ri-star-fill text-foreground-300"}`}></i>
                    ))}
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.comment}</p>
                <span className="text-xs text-foreground-400">{review.date}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-10 md:py-14 bg-background-100/70">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="max-w-[960px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                {aboutData.contact.title}
              </h2>
              <p className="text-foreground-500 text-sm mb-6">{aboutData.contact.subtitle}</p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">邮箱</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutData.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-phone-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">电话</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutData.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">地址</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutData.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-time-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">工作时间</p>
                    <p className="text-sm font-medium text-foreground-800">{aboutData.contact.workingHours}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-5">给我们留言</h3>
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm text-foreground-600 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-600 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm text-foreground-600 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>
                  <div className="honeypot-field">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    disabled={formState === "loading"}
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {formState === "loading" ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i> 提交中...
                      </span>
                    ) : "提交留言"}
                  </button>
                  {formState === "success" && (
                    <p className="text-sm text-accent-600 text-center mt-2">{formMessage}</p>
                  )}
                  {formState === "error" && (
                    <p className="text-sm text-red-500 text-center mt-2">{formMessage}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}